import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fridaveAPI, roundsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { ROUTES } from '../../utils/constants';

export default function FriDaveRounds() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [rounds, setRounds] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { fetchSeasons(); }, []);
  useEffect(() => { if (selectedSeason) fetchRounds(selectedSeason); }, [selectedSeason]);

  const fetchSeasons = async () => {
    try {
      const data = await fridaveAPI.getSeasons();
      const list = (Array.isArray(data) ? data : []).map(s => s.series_season || s);
      setSeasons(list);
      if (list.length > 0 && !list.includes(selectedSeason)) setSelectedSeason(list[0]);
    } catch (_) {}
  };

  const fetchRounds = async (season) => {
    try {
      setLoading(true);
      setError('');
      const data = await fridaveAPI.getRounds(season);
      setRounds(Array.isArray(data) ? data : []);
    } catch (_) {
      setError('Failed to load rounds');
      setRounds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartRound = async (roundId) => {
    try {
      await roundsAPI.start(roundId);
      fetchRounds(selectedSeason);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start round');
    }
  };

  const handleCloseEvent = async (roundId) => {
    if (!confirm('Close this event? This will finalise all scores.')) return;
    try {
      await roundsAPI.complete(roundId);
      fetchRounds(selectedSeason);
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to close event');
    }
  };

  const handleDeleteEvent = async (roundId, eventName, isLive) => {
    const warning = isLive
      ? `⚠️ DELETE LIVE ROUND\n\n"${eventName}" is currently in progress.\n\nAll scores entered so far will be permanently lost. This cannot be undone.\n\nAre you sure?`
      : `Delete "${eventName}"?\n\nAll scores and records will be permanently removed. This cannot be undone.`;
    if (!confirm(warning)) return;
    try {
      await roundsAPI.delete(roundId);
      fetchRounds(selectedSeason);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete event');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'in_progress' || status === 'active')
      return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full animate-pulse">LIVE</span>;
    if (status === 'completed')
      return <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full">Completed</span>;
    return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">Not Started</span>;
  };

  return (
    <div className="px-4 pb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900">Manage Events</h1>
        <div className="flex items-center gap-2">
          {seasons.length > 1 && (
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1 text-sm font-semibold text-gray-700 bg-white"
            >
              {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {isAuthenticated && (
            <button
              onClick={() => navigate(ROUTES.FRIDAVE_CREATE)}
              className="px-3 py-2 bg-gray-900 text-white text-sm font-bold rounded-lg hover:bg-gray-700 transition-colors"
            >
              + New
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">{error}</div>
      ) : rounds.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-200">
          <div className="text-5xl mb-3">⛳</div>
          <p className="text-gray-600 font-semibold">No events yet for {selectedSeason}</p>
          {isAuthenticated && (
            <button
              onClick={() => navigate(ROUTES.FRIDAVE_CREATE)}
              className="mt-4 px-6 py-3 bg-fd-primary text-white font-bold rounded-xl"
            >
              Create First Event
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {rounds.map((round) => {
            const isLive = round.status === 'in_progress' || round.status === 'active';
            const isCompleted = round.status === 'completed';
            const isNotStarted = !isLive && !isCompleted;

            return (
              <div key={round.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Round info */}
                <div className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900">{round.event_name || 'FriDave Event'}</h3>
                      {getStatusBadge(round.status)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(round.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                    {round.player_count > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">{round.player_count} players</p>
                    )}
                  </div>
                </div>

                {/* Admin actions */}
                {isAuthenticated && (
                  <div className="px-4 pb-3 flex gap-2">
                    {isNotStarted && (
                      <button
                        onClick={() => navigate(`/fridave/rounds/${round.id}/setup`)}
                        className="flex-1 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Set Up Players
                      </button>
                    )}
                    {isNotStarted && round.player_count > 0 && (
                      <button
                        onClick={() => handleStartRound(round.id)}
                        className="flex-1 py-2 bg-gray-900 text-white text-sm font-bold rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Start Round
                      </button>
                    )}
                    {isLive && (
                      <button
                        onClick={() => handleCloseEvent(round.id)}
                        className="flex-1 py-2 bg-gray-900 text-white text-sm font-bold rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Finish Round
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteEvent(round.id, round.event_name, isLive)}
                      className="py-2 px-3 bg-red-50 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-100 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
